import { create } from 'zustand'

/**
 * Zustand store that accumulates fetched timesheets keyed by month (YYYY-MM).
 * Timesheets are merged so navigating between months doesn't evict cached data.
 */
const useTimesheetsStore = create(set => ({
  /** @type {Record<string, object[]>} */
  timesheetsByMonth: {},

  /**
   * Merge newly fetched timesheets into the store for a given month key.
   * Duplicates are removed by _id.
   */
  mergeTimesheetsForMonth: (monthKey, timesheets) =>
    set(state => {
      const existing = state.timesheetsByMonth[monthKey] || []
      const existingIds = new Set(existing.map(ts => ts._id))
      const fresh = timesheets.filter(ts => !existingIds.has(ts._id))
      return {
        timesheetsByMonth: {
          ...state.timesheetsByMonth,
          [monthKey]: [...existing, ...fresh],
        },
      }
    }),

  /**
   * Upsert a single timesheet (e.g. after submit/reopen).
   * Derives the month key from the timesheet's startDate.
   */
  updateTimesheetInStore: timesheet =>
    set(state => {
      const monthKey = timesheet.startDate.slice(0, 7)
      const existing = state.timesheetsByMonth[monthKey] || []
      const updated = existing.some(ts => ts._id === timesheet._id)
        ? existing.map(ts => (ts._id === timesheet._id ? timesheet : ts))
        : [...existing, timesheet]
      return {
        timesheetsByMonth: {
          ...state.timesheetsByMonth,
          [monthKey]: updated,
        },
      }
    }),
}))

export default useTimesheetsStore
